import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, Checkbox, Paper, Avatar, Alert } from "@mui/material";
import { Grid, Stack } from "@mui/system";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import { useState } from 'react'
import { setSelectedRows } from '../../../../../../features/businessInsightsSlice';
import { useDispatch, useSelector } from 'react-redux';
import DataStatusAlert from '../../../../Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers';
import { useUpdateBusinessInsightsStatus } from '../../../../../../customHooks/useUpdateBusinessInsightsStatus';
import SimpleBackdrop from "../../../../../BackDrop/BackDrop";

interface Props {
  open: boolean;
  handleClose: () => void;
}

const ResolveInsightsPopup = ({ open, handleClose }: Props) => {
  const dispatch = useDispatch();
  const selectedRows = useSelector((state: any) => state?.businessInsights?.selectedRows);
  const [selected, setSelected] = useState<any[]>(selectedRows);
  const [comment, setComment] = useState<string>("");
  const isAllSelected = selected.length === selectedRows.length; // Check if all rows are selected

  const { loading, updateBusinessInsightsStatus } = useUpdateBusinessInsightsStatus("resolved"); // Custom hook for updating status(Resolved)  

  // Handle "Select All" checkbox
  const handleSelectAll = () => {
    setSelected(isAllSelected ? [] : [...selectedRows]);
  };

  // Handle individual row selection
  const handleSelectRow = (row: any) => {
    setSelected((prev) =>
      prev.some((r) => r === row)
        ? prev.filter((r) => r !== row)
        : [...prev, row]
    );
  };

  //-- Close the Resolve modal and clear the selected rows(unchecked checkboxes)
  const handleCloseResolveModal = () => {
    handleClose(); // Close modal if success
    dispatch(setSelectedRows([])); // Clear selected rows
  }

  //-- Request body for updating task status Resolved
  const resolveBodyActionItem = {
    tasks: selected.filter((task) => task.current_status !== "Done") // Only include not Done
      .map((task) => ({
        shared_uid: task.shared_uid,
        current_status: "Done"
      })),
  };

  //-- Clicking the Resolve button
  const handleActions = async () => {
    if (selected.length === 0) return;
    //-- this function for update status(MAKE API call for update status)
    updateBusinessInsightsStatus(resolveBodyActionItem, comment, handleCloseResolveModal);
  };

  return (
    <>
    {loading ? (
      <SimpleBackdrop />
    ) : (
      <>
      <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>Resolve Insights</DialogTitle>
      <DialogContent>
        <Paper sx={{
          width: "100%", overflowY: "auto", height: "auto", maxHeight: "225px", scrollbarWidth: "thin",
        }}
        >
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ padding: "4px" }}>Task Name</TableCell>
                <TableCell sx={{ padding: "4px" }}>Assignee</TableCell>
                <TableCell sx={{ padding: "4px" }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedRows?.map((row: any, index: number) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    ...(row.current_status === "Done" && {
                      backgroundColor: "#f5f5f5",
                      opacity: 0.6,
                    }),
                  }}
                >
                  <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                    <Checkbox
                      size="small"
                      color="secondary"
                      checked={selected.some((r) => r === row)}
                      onChange={() => handleSelectRow(row)}
                      disabled={row.current_status === "Done"}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: "4px" }}>{row?.data?.["query_output:sec_title"]?.value || "N/A"}</TableCell>
                  <TableCell sx={{ padding: "4px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", }}>
                      <Avatar sx={{ width: 22, height: 22, fontSize: 12, }}>
                        {row?.assignee_fullname.charAt(0).toUpperCase()}
                      </Avatar>
                      {row?.assignee_fullname}
                    </div>
                  </TableCell>
                  <TableCell sx={{ padding: "4px" }}>{row?.current_status && (
                    <DataStatusAlert status={row?.current_status} />
                  )}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <Typography gutterBottom marginBlock={2}>
          Add Comment
        </Typography>
        <Grid container>
          <Grid size={12}>
            <TextField
              color="secondary"
              fullWidth
              id="outlined-multiline-static"
              label="Content"
              placeholder="Explain why or how this task is resolved"
              multiline
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              required={selected?.length > 1}
              helperText="This field is mandatory"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
            {selected.length > 0 && selected.every((row) => row.current_status === "Done") && (
              <Alert severity="info" sx={{ mt: 2 }}>
                All selected tasks are already resolved (Done).
              </Alert>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: "20px" }}>
        <Button
          onClick={() => {
            handleClose();
            dispatch(setSelectedRows([]));
          }}
          color="secondary"
        >
          Cancel
        </Button>
        <Button
          data-tracker="115"
          variant="contained"
          color="secondary"
          loading={loading}
          loadingPosition="end"
          endIcon={loading && <SaveIcon />}
          disabled={
            selected.length === 0 ||
            (selected.length > 1 && !comment) ||
            selected.every((row) => row.current_status === "Done") // Disable if all selected are Done
          }
          onClick={() => handleActions()}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            Resolve
            <CheckIcon fontSize="small" />
          </Stack>
        </Button>
      </DialogActions>
    </Dialog>
      </>
    ) }
    </>
  )
}

export default ResolveInsightsPopup
